/**
 * Form Validation Utilities for One Voice
 *
 * This module provides validation functions for the prayer submission form:
 *   - Prayer form field validation (body, email, gender, phone, name, location)
 *   - Form data sanitization before submission
 *
 * Validation Rules:
 *   - body: Required, 10-250 characters
 *   - email: Required, must be valid format
 *   - gender: Required
 *   - phone: Optional, must be 7-15 digits if provided
 *   - firstName, lastName: Optional, max 100 characters each
 *   - location: Optional, max 100 characters
 *
 * @module lib/validation
 */

import { PrayerType, type SubmitterGender } from '@/types';

const VALID_GENDERS: SubmitterGender[] = ['male', 'female'];

const BODY_MAX_LENGTH = 250;

/**
 * Shape of the prayer submission form data.
 *
 * @property type - Prayer request or answered prayer
 * @property body - The prayer/praise text content
 * @property firstName - Optional submitter first name
 * @property lastName - Optional submitter last name
 * @property email - Required contact email
 * @property gender - Required submitter gender
 * @property phone - Optional contact phone number
 * @property location - Optional geographic location
 * @property is_anonymous - Whether to hide submitter identity
 */
export interface PrayerFormData {
  type: PrayerType | 'prayer_request' | 'praise_report' | '';
  body: string;
  firstName: string;
  lastName: string;
  email: string;
  gender: SubmitterGender | '';
  phone: string;
  location: string;
  is_anonymous: boolean;
}

/**
 * Validation error messages keyed by field name.
 * Only includes fields with errors.
 */
export interface ValidationErrors {
  type?: string;
  body?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  gender?: string;
  phone?: string;
  location?: string;
}

/**
 * Validates an email address format.
 *
 * @param email - Email string to validate
 * @returns True if valid format
 */
export function validateEmail(email: string): boolean {
  if (!email || email.trim() === '') return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
}

function isPraiseType(type: PrayerFormData['type']): boolean {
  const value = String(type);
  return value === 'praise_report' || value === PrayerType.PRAISE;
}

function bodyEmptyMessage(type: PrayerFormData['type']): string {
  return isPraiseType(type)
    ? 'Please share your answered prayer'
    : 'Please share your prayer request';
}

function validateBody(data: PrayerFormData): string | undefined {
  if (!data.body || data.body.trim() === '') {
    return bodyEmptyMessage(data.type);
  }
  if (data.body.trim().length < 10) {
    return 'Please provide at least 10 characters';
  }
  if (data.body.trim().length > BODY_MAX_LENGTH) {
    return `Please keep your message under ${BODY_MAX_LENGTH} characters`;
  }
  return undefined;
}

function validateIdentity(data: PrayerFormData): Pick<
  ValidationErrors,
  'firstName' | 'lastName' | 'location'
> {
  const errors: Pick<ValidationErrors, 'firstName' | 'lastName' | 'location'> = {};

  if (data.firstName && data.firstName.trim().length > 100) {
    errors.firstName = 'First name should be under 100 characters';
  }

  if (data.lastName && data.lastName.trim().length > 100) {
    errors.lastName = 'Last name should be under 100 characters';
  }

  if (data.location && data.location.trim().length > 100) {
    errors.location = 'Location should be under 100 characters';
  }
  return errors;
}

function validateContact(data: PrayerFormData): Pick<ValidationErrors, 'email' | 'gender' | 'phone'> {
  const errors: Pick<ValidationErrors, 'email' | 'gender' | 'phone'> = {};

  if (!data.email || data.email.trim() === '') {
    errors.email = 'Please enter your email address';
  } else if (!validateEmail(data.email)) {
    errors.email = 'Please enter a valid email address';
  }

  if (!data.gender || !VALID_GENDERS.includes(data.gender)) {
    errors.gender = 'Please select a gender';
  }

  if (data.phone && data.phone.trim() !== '') {
    const phoneDigits = data.phone.replace(/[\s\-\(\)\+\.]/g, '');
    if (!/^\d{7,15}$/.test(phoneDigits)) {
      errors.phone = 'Please enter a valid phone number';
    }
  }

  return errors;
}

/** Step 2 (Share): body required; name and location optional. */
export function validatePrayerFormStep2(data: PrayerFormData): ValidationErrors {
  const errors: ValidationErrors = {};
  const bodyError = validateBody(data);
  if (bodyError) errors.body = bodyError;
  return { ...errors, ...validateIdentity(data) };
}

/** Step 3 (Connect): email and gender required; phone optional. */
export function validatePrayerFormStep3(data: PrayerFormData): ValidationErrors {
  return validateContact(data);
}

/**
 * Validate prayer form data
 * @returns Object with field names as keys and error messages as values
 */
export function validatePrayerForm(data: PrayerFormData): ValidationErrors {
  return {
    ...validatePrayerFormStep2(data),
    ...validatePrayerFormStep3(data),
  };
}

/**
 * Check if form has any validation errors
 */
export function hasValidationErrors(errors: ValidationErrors): boolean {
  return Object.keys(errors).length > 0;
}

/**
 * Strip emojis, control characters, and zero-width/odd characters from text.
 * Collapses repeated spaces and trims. Use for prayer body and optional submitter fields.
 */
export function stripEmojisAndOddCharacters(text: string): string {
  if (typeof text !== 'string' || !text) return text;
  const stripped = text
    .replace(
      /[\p{Emoji_Presentation}\p{Extended_Pictographic}\p{Cc}\u200B-\u200D\uFEFF\u2060]/gu,
      ''
    )
    .replace(/\s+/g, ' ')
    .trim();
  return stripped;
}

/**
 * Sanitize form data before submission
 */
export function sanitizeFormData(data: PrayerFormData): PrayerFormData {
  const trimmedBody = data.body.trim();
  const trimmedFirst = data.firstName?.trim() || '';
  const trimmedLast = data.lastName?.trim() || '';
  const trimmedLocation = data.location?.trim() || '';
  return {
    ...data,
    body: stripEmojisAndOddCharacters(trimmedBody),
    firstName: stripEmojisAndOddCharacters(trimmedFirst),
    lastName: stripEmojisAndOddCharacters(trimmedLast),
    email: data.email?.trim().toLowerCase() || '',
    gender: data.gender,
    phone: data.phone?.trim() || '',
    location: stripEmojisAndOddCharacters(trimmedLocation),
  };
}
